import React from "react";
import {Collection} from "@bettorsignals/api";
import type {CategoryEnum, User, Signal as SignalType} from "@bettorsignals/api";
import {redirect, json} from "@remix-run/node";
import type {
    DataFunctionArgs,
    LoaderFunction,
    MetaFunction,
    HtmlMetaDescriptor
} from "@remix-run/node";
import {$api} from "~/api";
import type {TransportError} from "~/api";
import {OffersSlider, Signal} from "@bettorsignals/ui";
import {Link, useLoaderData, useOutletContext} from "@remix-run/react";
import {isBefore} from "date-fns";
import {useLinkTo} from "~/hooks/useLinkTo";
import {LinkTo} from "~/utils/LinkTo";
import {CATEGORIES} from "~/constants/Categories.dict";
import slugify from "slugify";
import type {WithOffersOutletContext} from "~/routes/__with-offers";
import {ArrowShortLeft} from "@bettorsignals/assets";

type LoaderData = {signal: SignalType & {author: User; isActive: boolean}};

export const loader: LoaderFunction = async ({params}: DataFunctionArgs) => {
    const {signalId} = params;
    const parametersSlug = params["*"];

    if (!signalId) {
        throw new Response("Signal Not Found", {
            status: 404
        });
    }

    const signal: LoaderData["signal"] = await $api
        .items(Collection.Signals)
        .readOne(signalId, {fields: ["*", "categories.*", "author.*", "images.*"]})
        .catch((error: TransportError) => {
            // https://github.com/directus/directus/issues/10137
            if (error?.response?.status === 403) {
                throw new Response("Signal Not Found", {
                    status: 404
                });
            } else {
                const message =
                    error?.response?.errors?.[0]?.message ?? "Error while retrieving signal data";
                const status = error?.response?.status ?? 500;

                throw new Response(message, {
                    status
                });
            }
        });

    const expectedSlug = slugify(signal.title, {lower: true, strict: true});
    const relativeSignalPagePath = LinkTo.signalPage(signal.id, signal.title);

    if (parametersSlug !== expectedSlug) return redirect(relativeSignalPagePath, {status: 301});

    return json<LoaderData>({
        signal: {
            ...signal,
            isActive: isBefore(new Date(), new Date(signal.dateExpires))
        }
    });
};

export const meta: MetaFunction = ({data}) => {
    if (!data) {
        return {
            title: "There was an error"
        };
    }

    const {
        signal: {id, title: signalTitle, author, content, confidence, categories}
    } = data as LoaderData;

    const category = categories.length > 1 ? "Parlay" : categories[0] || "Sports"; //Fallback to "Sports" if there ever be another collation issue
    const categoryString =
        //@ts-ignore junction collection is not typed. "item" is a PK of "categories".
        typeof category === "string" ? category : CATEGORIES[category.item as CategoryEnum].name;

    const description = content
        ? `${content
              .replace(/[\n\r]/g, "") //remove new line symbols
              .slice(0, 157)
              .trim()}...`
        : `${categoryString} betting tip by ${author.nickname} with ${Math.floor(
              (confidence / 5) * 100
          )}% confidence. Click to see the odds and details...`;

    const title = `${signalTitle} | BettorSignals.com`;

    const meta: HtmlMetaDescriptor = {
        title,
        description,
        author: author.nickname,
        meta: [
            {name: "twitter:card", content: "summary"},
            {name: "twitter:title", content: title},
            {
                name: "twitter:description",
                content: description
            },
            {name: "twitter:site", content: "@BettorSignals"},
            {
                name: "twitter:image",
                content: `${LinkTo.fullPageUrl()}/_static/social-cards/og-square.jpg`
            },
            {property: "og:type", content: "website"},
            {property: "og:title", content: title},
            {
                property: "og:description",
                content: description
            },
            {
                property: "og:url",
                content: `${LinkTo.fullPageUrl()}${LinkTo.signalPage(id, signalTitle)}`
            },
            {
                property: "og:image",
                content: `${LinkTo.fullPageUrl()}/_static/social-cards/og-large.jpg`
            }
        ]
    };

    return meta;
};

export default function SignalPage() {
    const {signal} = useLoaderData<LoaderData>();
    const {offers, bookmakers} = useOutletContext<WithOffersOutletContext>();
    const {linkTo} = useLinkTo();
    const {
        id,
        title,
        categories,
        content,
        confidence,
        author,
        datePublished,
        dateExpires,
        defaultOdds,
        isActive
    } = signal;
    return (
        <>
            <Link
                to={LinkTo.homePage()}
                className="flex items-center gap-3 text-2xl font-bold text-gray-900 md:text-3xl"
            >
                <ArrowShortLeft className="w-[18px]" />
                Browse all signals
            </Link>

            {!isActive && (
                <span className="relative top-2.5 text-center italic text-gray-600">
                    This pick is now expired.
                </span>
            )}

            <Signal
                isActive={isActive}
                disableShowMore={true}
                categories={categories.map((category) => {
                    const {name, IconOutline} =
                        //@ts-ignore junction collection is not typed. "item" is a PK of "categories".
                        CATEGORIES[category.item as CategoryEnum];

                    return {name, Icon: IconOutline};
                })}
                author={{
                    nickname: author.nickname,
                    avatarSrc:
                        author.avatar &&
                        linkTo.asset(author.avatar, {
                            width: 100,
                            height: 100,
                            quality: 100,
                            seoFriendlyName: `${author.nickname} avatar`
                        })
                }}
                datePublished={datePublished}
                dateExpires={dateExpires}
                confidence={confidence}
                title={title}
                content={content}
                shareUrl={`${LinkTo.fullPageUrl()}${LinkTo.signalPage(id, title)}`}
                odds={defaultOdds}
                bookmakers={bookmakers.map((bookmaker) => {
                    return {
                        url: bookmaker.url,
                        details: {
                            name: bookmaker.name,
                            logoSquare: bookmaker.logoSquare,
                            logoRectangle: bookmaker.logoRectangle,
                            colors: bookmaker.colors
                        },
                        signUpOffer: undefined,
                        odds: undefined,
                        deeplink: undefined
                    };
                })}
                trackingContext={{
                    category: categories
                        .map(
                            //@ts-ignore junction collection is not typed. "item" is a PK of "categories".
                            (category) => category.item as CategoryEnum
                        )
                        .join(","),
                    signalTitle: title,
                    signalId: id,
                    authorNickname: author.nickname,
                    authorId: author.id,
                    odds: defaultOdds || "-",
                    confidence
                }}
            />

            {offers.length > 0 && (
                <>
                    <div className="block md:hidden">
                        <h2 className="pb-1 text-xl font-bold text-gray-900">
                            Free Bets &amp; Offers
                        </h2>

                        <OffersSlider offers={offers} />
                    </div>
                </>
            )}
        </>
    );
}
